import { RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface Props {
  /** Runs the refresh — typically a `queryClient.invalidateQueries` call. The
   *  button shows a spinner until the returned promise settles. */
  onRefresh: () => void | Promise<void>;
  /** Accessible name; also the native tooltip. Names the action ("Refresh
   *  product data"), not the glyph. */
  label: string;
  /** Button style — `ghost` (default) for inline toolbars, `outline` to match a
   *  cluster of bordered icon buttons (e.g. a page header's action row). */
  variant?: 'ghost' | 'outline';
  className?: string | undefined;
  /** `data-testid` for the button (default `refresh-button`). */
  testId?: string;
}

/**
 * Icon-only refresh control that owns its own pending state, so callers just
 * supply the refresh action. The glyph is swapped for the Button's spinner while
 * in flight, so only one indicator ever shows. Shared by the product detail page
 * and every product modal so the affordance looks and behaves identically.
 */
export function RefreshIconButton({
  onRefresh,
  label,
  variant = 'ghost',
  className,
  testId = 'refresh-button',
}: Props) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  /** Awaits the caller's refresh so the spinner reflects the real settle time. */
  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }

  return (
    <Button
      variant={variant}
      size="icon"
      onClick={() => void handleRefresh()}
      isLoading={isRefreshing}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={cn('shrink-0 cursor-pointer disabled:cursor-not-allowed', className)}
    >
      {!isRefreshing && <RefreshCw className="h-4 w-4" aria-hidden="true" />}
    </Button>
  );
}
